/**
 * Helpers de las landings de captación por link (/partners y /agentes).
 *
 * Las tres puertas (foodie, referidor, agente) comparten la misma tubería:
 * un anuncio o un post lleva a la página con `?p=` y `?l=`, la página explica
 * lo que hay que saber y termina en el formulario interno
 * (components/marketing/PartnerLeadForm → /api/partners/leads). Ver
 * docs/marketing/mr-marketing/PLAN-EMBUDOS-FOODIES-AGENTES-REFERIDOS.md.
 */

export const PARTNER_PERFILES = ["foodie", "referidor"] as const
export type PartnerPerfil = (typeof PARTNER_PERFILES)[number]

/** `?p=` de la URL → perfil. Solo `referidor` (o `referidores`) abre esa puerta;
 *  sin `?p=` o con cualquier otra cosa, la página abre en foodie, que es la
 *  puerta que Samuel empuja desde su red social. */
export function parsePerfil(raw: string | string[] | undefined | null): PartnerPerfil {
  const v = (Array.isArray(raw) ? raw[0] : raw)?.trim().toLowerCase()
  return v === "referidor" || v === "referidores" ? "referidor" : "foodie"
}

/** `?l=` de la URL → idioma forzado (los anuncios en español llevan `l=es`). */
export function forcedLangFrom(raw: string | string[] | undefined | null): "es" | "en" | null {
  const v = (Array.isArray(raw) ? raw[0] : raw)?.trim().toLowerCase()
  return v === "es" ? "es" : v === "en" ? "en" : null
}

/**
 * Título, entradilla y puntos de la sección del formulario de /partners.
 * `registro` = puerta referidor con REFERRALS_PUBLIC_ENABLED encendido (el
 * registro propio del programa); en cualquier otro caso, el texto de siempre.
 */
export function formSectionCopy(
  registro: boolean,
  t: (en: string, es: string) => string,
): { title: string; lead: string; points: string[] } {
  if (!registro) {
    return {
      title: t("Call me", "Quiero que me llamen"),
      lead: t("Leave your number and we call you. Fifteen minutes, no commitment.", "Deja tu número y te llamamos. Quince minutos, sin compromiso."),
      points: [
        t("Who you are and what you do.", "Quién eres y a qué te dedicas."),
        t("Which restaurant owners you know.", "Qué dueños de restaurante conoces."),
        t("How the connection works, and your link.", "Cómo funciona la conexión, y tu link."),
      ],
    }
  }
  return {
    title: t("Get your referral code", "Pide tu código de referidor"),
    lead: t(
      "Leave your details and, if you want, the restaurant you'd like to introduce. We review every partner by hand.",
      "Deja tus datos y, si quieres, el restaurante que quieres presentar. Revisamos a cada socio a mano."
    ),
    points: [
      t("You sign up with your own details.", "Te inscribes con tus propios datos."),
      t("You can introduce your first restaurant right now.", "Puedes presentar tu primer restaurante desde ya."),
      t("When we approve you, your code and link are active.", "Cuando te aprobamos, tu código y tu link quedan activos."),
    ],
  }
}
